import { Table, Button, Input } from 'antd';
import React, {useCallback, useEffect, useState, useMemo} from 'react';
import { tablePreparation } from '../../services/dataHandling';
import RevenueChart from '../charts/revenueChart';
import TableCellWithEvolution from '../layout/tableCellWithEvolution';
import { getComparisonData } from '../../stateManagement/actions/report';
import HighlightedCell from '../layout/highlightedCell';
import { useDispatch, useSelector } from "react-redux";
import { StringUtils } from '../../utils/strings';
import { DownloadOutlined } from '@ant-design/icons';
import { exportToXLSX } from '../../services/exportReport';
import { useLocation } from "react-router-dom";
import { NANO_AGREEMENT, PERIOD_MACROS } from '../../utils/constants';

const getSimpleColumns = (pagination, period, activeHighlightConditions, activeFilter) => [
   {
    title: "#",
    dataIndex: "index",
    key: "index",
    render:  (text, record, index) => (pagination.current - 1) * pagination.pageSize + index + 1,
    width: 50
  },
  {
    title: 'Dimension',
    dataIndex: 'dimension',
    sorter: (a, b) => a.dimension - b.dimension,
  },
  {
    title: 'Revenue (€)',
    dataIndex: 'revenue',
    align: "right",
    sorter: (a, b) => a.revenue - b.revenue,
    render: (revenue, record) => checkIfNanoAdjustmentRequired(activeHighlightConditions, record, activeFilter) ? <HighlightedCell cellValue={() => StringUtils.getUSnumbersFormating(revenue, true)} tooltipText={`Adjusted value based on guaranteed CPM: ${adjustValuesBasedOnNanoAgreement(activeHighlightConditions, record, activeFilter).revenue} EUR`}/> : StringUtils.getUSnumbersFormating(revenue, true),
    defaultSortOrder: 'descend',
    width: 140
  },
  {
    title: 'Rev.share (%)',
    dataIndex: 'revenueShare',
    sorter: (a, b) => a.revenueShare - b.revenueShare,
    width: 140
  },
  {
    title: 'Rev.chart',
    dataIndex: 'revenueChart',
    render: (a) => <RevenueChart chartData={a} period={period}/>,
    width: 160
  },
  {
    title: 'Impressions',
    dataIndex: 'impressions',
    align: "right",
    sorter: (a, b) => a.impressions - b.impressions,
    render: (impressions) => StringUtils.getUSnumbersFormating(impressions),
    width: 140
  },
  {
    title: 'CPM (€)',
    dataIndex: 'cpm',
    sorter: (a, b) => a.cpm - b.cpm,
    render: (cpm, record) => checkIfNanoAdjustmentRequired(activeHighlightConditions, record, activeFilter) ? <HighlightedCell cellValue={cpm} tooltipText={`Current value is lower than guaranteed CPM: ${adjustValuesBasedOnNanoAgreement(activeHighlightConditions, record, activeFilter).cpm} EUR. Settlement value will be ${adjustValuesBasedOnNanoAgreement(activeHighlightConditions, record, activeFilter).cpm} EUR`}/> : cpm,
    width: 140
  },
  {
    title: 'CTR (%)',
    dataIndex: 'ctr',
    sorter: (a, b) => a.ctr - b.ctr,
    width: 140
  },
];

const getComparisonColumns = () => [
  {
    title: 'Revenue (€)',
    dataIndex: 'revenue',
    align: "right",
    sorter: (a, b) => a.revenue[0] - b.revenue[0],
    defaultSortOrder: 'descend',
    render: (revenue) => <TableCellWithEvolution cellValue={revenue[0]} evolutionValue={revenue[1]} refValue={revenue[2]} fillWithZeros={true}/>,
    width: 180
  },
  {
    title: 'Impressions',
    dataIndex: 'impressions',
    align: "right",
    sorter: (a, b) => a.impressions[0] - b.impressions[0],
    render: (impressions) => <TableCellWithEvolution cellValue={impressions[0]} evolutionValue={impressions[1]} refValue={impressions[2]}/>,
    width: 180
  },
  {
    title: 'CPM (€)',
    dataIndex: 'cpm',
    sorter: (a, b) => a.cpm[0] - b.cpm[0],
    render: (cpm) => <TableCellWithEvolution cellValue={cpm[0]} evolutionValue={cpm[1]} refValue={cpm[2]}/>,
    width: 160
  },
  {
    title: 'CTR (%)',
    dataIndex: 'ctr',
    sorter: (a, b) => a.ctr[0] - b.ctr[0],
    render: (ctr) => <TableCellWithEvolution cellValue={ctr[0]} evolutionValue={ctr[1]} refValue={ctr[2]} />,
    width: 160
  },
];

//Custom functions to select when to highlight Nano records, based on current CPM
const checkIfNanoAdjustmentRequired = (activeHighlightConditions, record, activeFilter) => {
  //Just as a backup, we'll keep the function, but for now it will always return false
  if (1) return false;
  //Check if we have the highlight view conditions
  const isCorrectView = activeHighlightConditions();
  if (!isCorrectView) return false;
  //Build phase
  const {cpm, dimension} = record;
  if(isCorrectView === 'NanoView'){
    if(NANO_AGREEMENT.NO_FIXED_CPM_WEBSITES.includes(dimension)) return false;
    if(NANO_AGREEMENT.LOW_CPM_WEBSITES.includes(dimension)) return Number(cpm) < NANO_AGREEMENT.LOW_CPM ? true : false;
    if(Number(cpm) < NANO_AGREEMENT.NORMAL_CPM) return true;
  }
  if(isCorrectView === 'WebsiteView'){
    if(dimension !== "Nano") return false;
    const currentWebsite = activeFilter.replace('wbsCrossBy', '');
    if(NANO_AGREEMENT.NO_FIXED_CPM_WEBSITES.includes(currentWebsite)) return false;
    if(NANO_AGREEMENT.LOW_CPM_WEBSITES.includes(currentWebsite)) return Number(cpm) < NANO_AGREEMENT.LOW_CPM ? true : false;
    if(Number(cpm) < NANO_AGREEMENT.NORMAL_CPM) return true;
  }
}

const adjustValuesBasedOnNanoAgreement = (activeHighlightConditions, record, activeFilter) => {
  //Check if we have the highlight view conditions
  const currentView = activeHighlightConditions();
  //Check if we have the highlight view conditions
  const {cpm, impressions, dimension} = record;
  if (Number(cpm) === 0) return 0;
  if(currentView === 'NanoView'){
    if(NANO_AGREEMENT.LOW_CPM_WEBSITES.includes(dimension)) return {cpm: NANO_AGREEMENT.LOW_CPM, revenue: StringUtils.getUSnumbersFormating((impressions / 1000) * NANO_AGREEMENT.LOW_CPM, true)};
  }
  if(currentView === 'WebsiteView'){
      const currentWebsite = activeFilter.replace('wbsCrossBy', '');
      if(NANO_AGREEMENT.LOW_CPM_WEBSITES.includes(currentWebsite)) return {cpm: NANO_AGREEMENT.LOW_CPM, revenue: StringUtils.getUSnumbersFormating((impressions / 1000) * NANO_AGREEMENT.LOW_CPM, true)};
  }
  return {cpm: NANO_AGREEMENT.NORMAL_CPM, revenue: StringUtils.getUSnumbersFormating((impressions / 1000) * NANO_AGREEMENT.NORMAL_CPM, true)}
}

const SummaryTable = ({fullData, dashboardFilters, partition, reportScope}) => {

  const dispatch = useDispatch();
  const location = useLocation();
  const [comparisonData, setComparisonData] = useState([]);

  const isDealsPage = location.pathname === '/deals' || location.pathname === '/thd/deals';

  // Table search state
  const [searchInput, setSearchInput] = useState(""); // live input
  const [searchText, setSearchText] = useState("");   // debounced value
  const [pagination, setPagination] = useState({current: 1, pageSize: 20});
  const handleTableChange = (newPagination) => {
    setPagination(newPagination);
  };

  // Extrating report-related state
  const reportReducer = useSelector((state) => state.reportReducer);
  const {dimension, comparison, period, filterBy: legacyFilterBy, publisherView} = reportReducer;
    const filterBy = legacyFilterBy || '';
  
  //Check if Nano is visible
  const activeHighlightConditions = useCallback(() => {
    if (period !== PERIOD_MACROS.CURRENT_MONTH && period !== PERIOD_MACROS.LAST_MONTH) return false;
    if (filterBy === 'provCrossByNano' && dimension === 'website') return 'NanoView';
    if (filterBy?.includes('wbsCrossBy') && dimension === 'provider') return 'WebsiteView';
    return false;
  }, [filterBy, dimension, period])

  // Prepare data based on current dimension
  const data = tablePreparation(fullData, dimension);

    // Use the same applied filters for both periods and discard stale responses.
    useEffect(() => {
        let active = true;
        setComparisonData([]);
        if (comparison && publisherView) {
            dispatch(getComparisonData(
                {period, dimension, filterBy, publisherView, isDealsPage, dashboardFilters, partition, reportScope},
                result => {if (active) setComparisonData(result);}
            ));
        }
        return () => {active = false;};
    }, [dispatch, period, dimension, filterBy, comparison, publisherView, isDealsPage, dashboardFilters, partition, reportScope]);

  // Debounce effect
  useEffect(() => {
    const handler = setTimeout(() => {
      setSearchText(searchInput);
    }, 500); // <-- debounce delay in ms

    return () => clearTimeout(handler);
  }, [searchInput]);

  // Export data to Excel
  const handleExportData = () => exportToXLSX(data);  

  // Determine columns and data source based on comparison mode
  const columns = useMemo(() => {
  if (comparison) {
    return [
      {
        title: "#",
        dataIndex: "index",
        key: "index",
        render: (text, record, index) => (pagination.current - 1) * pagination.pageSize + index + 1,
        width: 50
      },
      {
        title: 'Dimension',
        render: (record) => record[dimension],
        sorter: (a, b) => (a[dimension] ?? '').localeCompare(b[dimension] ?? ''),
      },
      ...getComparisonColumns()
    ];
  } else {
    return getSimpleColumns(pagination, period, activeHighlightConditions, filterBy);
  }
}, [comparison, dimension, pagination, period, activeHighlightConditions, filterBy]);

  const tableData = comparison ? comparisonData : data;

  // Filtered data
  const filteredData = useMemo(() => {
    if (!searchText) return tableData;
    return tableData.filter((item) => {
      return comparison 
        ? item[dimension].toLowerCase().includes(searchText.toLowerCase())
        : item.dimension.toLowerCase().includes(searchText.toLowerCase())
      });
  }, [searchText, tableData, dimension, comparison]);

  return (
    <div className="table-container">
      <div className="spaced-between-container" style={{width: "100%"}}>
        <Input.Search
          placeholder="Search in table"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          style={{ marginBottom: 16, width: 300 }}
          allowClear
        />
        <Button 
          className="std-button" 
          size="large"
          onClick={handleExportData}
        >
          <DownloadOutlined /> Export
        </Button>
      </div>
      <Table 
        key={comparison ? 'comparison' : 'simple'}
        className="summary-table" 
        columns={columns}
        dataSource={filteredData} 
        scroll={{ x: '70vw' }}
        pagination={{
          ...pagination,
          showSizeChanger: true,
          showTotal: (total) => `Total: ${total}`
        }}
        onChange={handleTableChange}
      />
    </div>
  );
};
export default SummaryTable;